// ── Sessions tab — the cohort schedule (Wave 3, REAL read) ──────────────────
// Reads GET /cohorts/:id/sessions via sessions.listForCohort wrapped in
// useHonestQuery -> CohortTabShell. READ tab — write flows owned by Wave 6.

import { CollectionPagination, CollectionToolbar, DataTableV2 } from "@vjal/ui";
import { useMemo } from "react";

import { useHonestQuery } from "../../../components/honest-state";
import { getCoachingServices } from "../../../services/coaching-service-provider";
import { coachingQueryKeys } from "../../../hooks/coaching-query-keys";
import type { CoachingSessionDto, ListResponse } from "../../../services/coaching-dtos";
import { CohortTabShell } from "./cohort-tab-shell";
import { useCohortSessionsCollection } from "./cohort-sessions-collection";

const services = getCoachingServices();

export function CohortSessionsTab({ cohortId }: { cohortId: string }) {
  const query = useHonestQuery<ListResponse<CoachingSessionDto>>(
    coachingQueryKeys.list("sessions.listForCohort", { cohort_id: cohortId }),
    () => services.sessions.listForCohort(cohortId)
  );
  const allRows = useMemo(() => query.data?.items ?? [], [query.data?.items]);
  const collection = useCohortSessionsCollection(allRows);

  return (
    <CohortTabShell
      title="Sessions"
      subtitle="Scheduled sessions for this cohort. Creating sessions and series is managed in the Sessions area."
      posture={query.posture}
      gate={query.gate}
      reference={query.reference}
      onRetry={query.retry}
      emptyTitle="No sessions scheduled"
      emptyMessage="Sessions scheduled for this cohort will appear here."
    >
      <CollectionToolbar
        collection={collection}
        searchPlaceholder="Filter sessions"
        className="rounded-xl border border-line"
        stableChipNames
      />
      <div className="rounded-xl border border-line bg-white">
        <DataTableV2
          collection={collection}
          caption="Cohort sessions"
          emptyTitle="No sessions match your search"
          emptyMessage="Clear or change the filters above to widen the view."
        />
        {collection.uiState === "ready" && <CollectionPagination collection={collection} className="px-4" />}
      </div>
    </CohortTabShell>
  );
}